import React, { useState, useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import './NewActivityModal.css';
import { useGeolocationService } from '../contexts/GeolocationServiceContext';
import { useVideoService } from '../contexts/VideoServiceContext';
import DiagonalDumbbellIcon from './activities/DiagonalDumbbellIcon';
import {
HeartIcon,
FireIcon,
SunIcon,
ScaleIcon,
ArrowPathIcon,
PhotoIcon,
ShoppingBagIcon,
ChartBarIcon,
ClipboardDocumentListIcon,
VideoCameraIcon,
} from '@heroicons/react/24/outline';

export const iconMap = {
  cardio: <HeartIcon className="new-activity-icon" />,
  strength: <FireIcon className="new-activity-icon" />,
  flexibility: <SunIcon className="new-activity-icon" />,
  balance: <ScaleIcon className="new-activity-icon" />,
  workout: <DiagonalDumbbellIcon className="new-activity-icon" aria-hidden="true" />,
  redo: <ArrowPathIcon className="new-activity-icon" />,
  media: <PhotoIcon className="new-activity-icon" />,
  meal: <ShoppingBagIcon className="new-activity-icon" />,
  metric: <ChartBarIcon className="new-activity-icon" />,
  survey: <ClipboardDocumentListIcon className="new-activity-icon" />,
  ai: <VideoCameraIcon className="new-activity-icon" />,
};

const NewActivityModal = ({ onAdd, close, backendService, initialOption = 'redo' }) => {
  const { position } = useGeolocationService() || {};
  const videoService = useVideoService();
  const [previousActivities, setPreviousActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const activitiesPerPage = 10;
  const [selectedOption, setSelectedOption] = useState(initialOption);
  const [workoutName, setWorkoutName] = useState('');
  const [mediaName, setMediaName] = useState('');
  const [metricName, setMetricName] = useState('');
  const [mealName, setMealName] = useState('');
  const [surveyName, setSurveyName] = useState('');
  const [aiCircuit, setAiCircuit] = useState({ rounds: 3, target_repetitions: 5, rest_seconds: 30 });
  const [aiError, setAiError] = useState('');
  const [isCreatingAiWorkout, setIsCreatingAiWorkout] = useState(false);

  const isCreateEnabled = (() => {
    if(selectedOption === 'redo') return !!selectedActivity;
    if(selectedOption === 'newWorkout') return workoutName.trim().length > 0;
    if(selectedOption === 'newMedia') return mediaName.trim().length > 0;
    if(selectedOption === 'newMetric') return metricName.trim().length > 0;
    if(selectedOption === 'newMeal') return mealName.trim().length > 0;
    if(selectedOption === 'newSurvey') return surveyName.trim().length > 0;
    if(selectedOption === 'aiSupervised') return !isCreatingAiWorkout;
    return false;
  })();

  useEffect(() => {
    const fetchData = async () => {
      setCurrentPage(1);
      if(selectedOption === 'redo'){
        const initialPreviousActivities = await fetchActivities(1, true);
        if (initialPreviousActivities.length === 0) {
          // Optionally handle empty list for redo
        }
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  useEffect(() => {
    if (selectedOption === 'redo' && hasMore && previousActivities.length > 0) {
      const scrollContainer = document.getElementById('new-activity-redo-scroll');
      if (scrollContainer && scrollContainer.scrollHeight <= scrollContainer.clientHeight) {
        fetchMoreActivities();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousActivities, hasMore, selectedOption]);

  const fetchActivities = async (page = 1, reset = false) => {
    try {
      const data = await backendService.fetchActivities(page, activitiesPerPage);
      const activities = data.activities;

      const mergeAndSortActivities = prevActivities => {
        const newActivities = reset ? activities : [...prevActivities, ...activities];
        const uniqueActivities = newActivities.filter((activity, index, self) =>
          index === self.findIndex((a) => a._id === activity._id)
        );
        return uniqueActivities.sort((a, b) => b._id - a._id);
      }
      const updatedActivities = mergeAndSortActivities(previousActivities);
      setPreviousActivities(updatedActivities);
      setHasMore(page < data.totalPages);

      return updatedActivities;
    } catch (error) {
      console.error('Error fetching previous activities:', {
        message: error.message,
        stack: error.stack,
        response: error.response,
      });
      return [];
    }
  };

  const startOptionalWorkoutStream = (workout) => {
    if (workout?.type !== 'workout' || workout.status !== 'ongoing') return;
    videoService.startWorkoutBroadcast(workout._id).catch(error => {
      console.warn('[NewActivityModal] Workout started without optional video stream:', error.message);
    });
  };

  const cloneActivity = async (type, activityId) => {
    try {
      let newActivity = await backendService.createActivity(type, activityId);

      if (newActivity.type === 'workout' && newActivity.status === 'pending') {
        try {
          const startedActivity = await backendService.startWorkout(newActivity._id, position);
          if (startedActivity) {
            newActivity = startedActivity;
          }
        } catch (startError) {
          console.error('Error auto-starting cloned workout:', startError);
        }
      }

      startOptionalWorkoutStream(newActivity);
      onAdd(newActivity);
    } catch (error) {
      console.error('Error cloning activity:', {
        message: error.message,
        stack: error.stack,
        response: error.response,
      });
      alert(error.response?.data?.error || 'An error occurred while cloning the activity.');
    }
  };

  const startNewWorkout = async (activityId, name = null) => {
    try {
      let newActivity = activityId
        ? await backendService.startNewWorkout(activityId, null, position)
        : await backendService.startNewWorkout(null, name, position);
      
      if (newActivity.type === 'workout' && newActivity.status === 'pending') {
        try {
          const startedActivity = await backendService.startWorkout(newActivity._id, position);
          if (startedActivity) {
            newActivity = startedActivity;
          }
        } catch (startError) {
          console.error('Error auto-starting new workout:', startError);
          // Continue with the pending activity if start fails
        }
      }

      startOptionalWorkoutStream(newActivity);
      onAdd(newActivity);
    } catch (error) {
      console.error('Error starting new workout:', {
        message: error.message,
        stack: error.stack,
        response: error.response,
      });
      alert(error.response?.data?.error || 'An error occurred while starting the workout.');
    }
  };

  const createNewMedia = async (name) => {
    try {
      const newActivity = await backendService.createActivity('media', null, name);
      onAdd(newActivity);
    } catch (error) {
      console.error('Error creating media:', {
        message: error.message,
        stack: error.stack,
        response: error.response,
      });
      alert(error.response?.data?.error || 'An error occurred while creating the media.');
    }
  };

  const createNewMetric = async (name) => {
    try {
      const newActivity = await backendService.createMetric(name);
      onAdd(newActivity);
    } catch (error) {
      console.error('Error creating metric:', {
        message: error.message,
        stack: error.stack,
        response: error.response,
      });
      alert(error.response?.data?.error || 'An error occurred while creating the metric.');
    }
  };

  const createNewMeal = async (name) => {
    try {
      const newActivity = await backendService.createMeal(name);
      onAdd(newActivity);
    } catch (error) {
      console.error('Error creating meal:', {
        message: error.message,
        stack: error.stack,
        response: error.response,
      });
      alert(error.response?.data?.error || 'An error occurred while creating the meal.');
    }
  };

  const createNewSurvey = async (name) => {
    try {
      const newActivity = await backendService.createActivity('survey', null, name);
      onAdd(newActivity);
    } catch (error) {
       console.error('Error creating survey:', {
        message: error.message,
        stack: error.stack,
        response: error.response,
      });
      alert(error.response?.data?.error || 'An error occurred while creating the survey.');
    }
  };

  const createNewAiSupervisedWorkout = async () => {
    setAiError('');
    setIsCreatingAiWorkout(true);
    try {
      const newActivity = await backendService.createAiSupervisedWorkout(aiCircuit);
      onAdd(newActivity);
      return true;
    } catch (error) {
      console.error('Error creating AI-supervised workout:', error);
      setAiError(error.response?.data?.error || error.message || 'Could not create the AI-supervised workout.');
      return false;
    } finally {
      setIsCreatingAiWorkout(false);
    }
  };

  const fetchMoreActivities = () => {
    setCurrentPage(prevPage => prevPage + 1);
    fetchActivities(currentPage + 1);
  };

  const handleCreate = async () => {
    if(selectedOption === 'redo'){
      if(!selectedActivity) {
        alert("Please select an activity to redo.");
        return;
      }
      await cloneActivity(selectedActivity.type, selectedActivity._id);
    } else if(selectedOption === 'newWorkout'){
      if(!workoutName.trim()){
        alert("Please enter a workout name.");
        return;
      }
      await startNewWorkout(null, workoutName);
    } else if(selectedOption === 'newMedia'){
      if(!mediaName.trim()){
        alert("Please enter a media name.");
        return;
      }
      await createNewMedia(mediaName);
    } else if(selectedOption === 'newMetric'){
      if(!metricName.trim()){
        alert("Please enter a metric name.");
        return;
      }
      await createNewMetric(metricName);
    } else if(selectedOption === 'newMeal'){
      if(!mealName.trim()){
        alert("Please enter a meal name.");
        return;
      }
      await createNewMeal(mealName);
    } else if(selectedOption === 'newSurvey'){
      if(!surveyName.trim()){
        alert("Please enter a survey name.");
        return;
      }
      await createNewSurvey(surveyName);
    } else if(selectedOption === 'aiSupervised'){
      if (await createNewAiSupervisedWorkout()) close();
      return;
    }
    close();
  };

  return (
    <div className="new-activity-modal">
      <div className="new-activity-type-selector">
        {['redo', 'newWorkout', 'aiSupervised', 'newMeal', 'newMedia', 'newMetric', 'newSurvey'].map((option) => {
          const labelMap = {
            redo: 'Redo activity',
            newWorkout: 'New Workout',
            aiSupervised: 'AI-supervised workout (PoC)',
            newMeal: 'New Meal',
            newMedia: 'New Media',
            newMetric: 'New Metric',
            newSurvey: 'New Survey',
          };
          const isActive = selectedOption === option;
          return (
            <button 
              key={option}
              onClick={() => setSelectedOption(option)}
              className={`new-activity-type-button ${isActive ? 'active' : ''}`}
            >
              {option === 'aiSupervised' ? iconMap.ai : (iconMap[option.replace('new', '').toLowerCase()] || null)}
              <span className="new-activity-type-label">
                {labelMap[option]}
              </span>
            </button>
          );
        })}
      </div>

      <div className="new-activity-body">
        {selectedOption === 'redo' && (
          <div className="new-activity-redo">
            <div className="new-activity-help">Choose a recent activity to redo</div>
            <div id="new-activity-redo-scroll" className="new-activity-redo-scroll">
              <InfiniteScroll
                dataLength={previousActivities.length}
                next={fetchMoreActivities}
                hasMore={hasMore}
                scrollableTarget="new-activity-redo-scroll"
              >
                <div className="new-activity-source-list">
                  {previousActivities.map(activity => (
                    <div
                      key={activity._id}
                      className={`activity-item ${selectedActivity && selectedActivity._id === activity._id ? 'selected' : ''}`}
                      onClick={() => setSelectedActivity(activity)}
                    >
                      <div className="new-activity-source-row">
                        <span className="new-activity-source-icon">{iconMap[activity.type] || null}</span>
                        <span className="new-activity-source-name">{activity.name}</span>
                        <span className="new-activity-source-meta">({activity.type})</span>
                        <span className="new-activity-source-date">{new Date(activity.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </InfiniteScroll>
            </div>
          </div>
        )}

        {selectedOption === 'newWorkout' && (
          <div className="new-activity-form">
            <label className="new-activity-label">Workout Name</label>
            <input
              className="new-activity-input"
              type="text"
              value={workoutName}
              onChange={(e) => setWorkoutName(e.target.value)}
              placeholder="e.g. Push Day"
            />
          </div>
        )}
        {selectedOption === 'aiSupervised' && (
          <div className="new-activity-form ai-supervised-create-form">
            <div className="new-activity-help">
              <strong>Pushups only.</strong> Set your phone side-on so your full body stays visible. Frames stay on this device for pose processing; optional livestreaming is separate.
            </div>
            <div className="ai-supervised-circuit-fields">
              <label className="new-activity-label" htmlFor="ai-rounds">Rounds
                <input id="ai-rounds" className="new-activity-input" type="number" min="1" max="10" value={aiCircuit.rounds}
                  onChange={(event) => setAiCircuit(current => ({ ...current, rounds: Number(event.target.value) }))} />
              </label>
              <label className="new-activity-label" htmlFor="ai-repetitions">Pushups per round
                <input id="ai-repetitions" className="new-activity-input" type="number" min="1" max="50" value={aiCircuit.target_repetitions}
                  onChange={(event) => setAiCircuit(current => ({ ...current, target_repetitions: Number(event.target.value) }))} />
              </label>
              <label className="new-activity-label" htmlFor="ai-rest">Rest (seconds)
                <input id="ai-rest" className="new-activity-input" type="number" min="5" max="180" value={aiCircuit.rest_seconds}
                  onChange={(event) => setAiCircuit(current => ({ ...current, rest_seconds: Number(event.target.value) }))} />
              </label>
            </div>
            {aiError && <div className="new-activity-error" role="alert">{aiError}</div>}
          </div>
        )}
        {selectedOption === 'newMedia' && (
          <div className="new-activity-form">
            <label className="new-activity-label">Media Name</label>
            <input
              className="new-activity-input"
              type="text"
              value={mediaName}
              onChange={(e) => setMediaName(e.target.value)}
              placeholder="e.g. Progress photo"
            />
          </div>
        )}
        {selectedOption === 'newMetric' && (
          <div className="new-activity-form">
            <label className="new-activity-label">Metric Name</label>
            <input
              className="new-activity-input"
              type="text"
              value={metricName}
              onChange={(e) => setMetricName(e.target.value)}
              placeholder="e.g. Bodyweight"
            />
          </div>
        )}
        {selectedOption === 'newMeal' && (
          <div className="new-activity-form">
            <label className="new-activity-label">Meal Name</label>
            <input
              className="new-activity-input"
              type="text"
              value={mealName}
              onChange={(e) => setMealName(e.target.value)}
              placeholder="e.g. Lunch"
            />
          </div>
        )}
        {selectedOption === 'newSurvey' && (
          <div className="new-activity-form">
            <label className="new-activity-label">Survey Name</label>
            <input
              className="new-activity-input"
              type="text"
              value={surveyName}
              onChange={(e) => setSurveyName(e.target.value)}
              placeholder="e.g. Daily Check-in"
            />
          </div>
        )}
      </div>
      
      <div className="button-container">
        <button className="btn-add" onClick={handleCreate} disabled={!isCreateEnabled}>Create</button>
        <button className="btn-cancel" onClick={close}>Cancel</button>
      </div>
    </div>
  );
};

export default NewActivityModal;